import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ChartViewer from './ChartViewer';

interface Chart {
  id: string;
  name: string;
  path: string;
  description?: string;
  category: 'overview' | 'growth' | 'retention' | 'activity';
}

const charts: Chart[] = [
  {
    id: 'score_distribution',
    name: 'Score Distribution',
    path: '/charts/score_distribution.html',
    description: 'AI evaluation score distribution histogram',
    category: 'overview'
  },
  {
    id: 'score_boxplot',
    name: 'Score Distribution (Box Plot)',
    path: '/charts/score_boxplot.html',
    description: 'Box plot visualization of score distribution',
    category: 'overview'
  },
  {
    id: 'mode_comparison',
    name: 'Mode Comparison',
    path: '/charts/mode_comparison.html',
    description: 'Score comparison by mode',
    category: 'overview'
  },
  {
    id: 'score_trend',
    name: 'Score Trend',
    path: '/charts/score_trend.html',
    description: 'Overall score trend analysis',
    category: 'overview'
  },
  {
    id: 'growth_clusters',
    name: 'Growth Pattern Clusters',
    path: '/charts/growth_clusters.html',
    description: 'User growth patterns identified through K-Means clustering',
    category: 'growth'
  },
  {
    id: 'growth_rate_distribution',
    name: 'Growth Rate Distribution',
    path: '/charts/growth_rate_distribution.html',
    description: 'Distribution of growth rates by user',
    category: 'growth'
  },
  {
    id: 'cluster_growth_comparison',
    name: 'Cluster Growth Comparison',
    path: '/charts/cluster_growth_comparison.html',
    description: 'Growth rate comparison by cluster',
    category: 'growth'
  },
  {
    id: 'score_trends',
    name: 'Sample User Score Trends',
    path: '/charts/score_trends.html',
    description: 'Score trends of representative users',
    category: 'growth'
  },
  {
    id: 'retention_curve',
    name: 'Retention Curve',
    path: '/charts/retention_curve.html',
    description: 'Weekly retention rate trends',
    category: 'retention'
  },
  {
    id: 'churn_comparison',
    name: 'Churn Analysis',
    path: '/charts/churn_comparison.html',
    description: 'Comparison of churned vs retained users',
    category: 'retention'
  },
  {
    id: 'daily_activity',
    name: 'Daily Activity',
    path: '/charts/daily_activity.html',
    description: 'Daily user activity patterns',
    category: 'activity'
  },
  {
    id: 'day_of_week_activity',
    name: 'Day of Week Activity',
    path: '/charts/day_of_week_activity.html',
    description: 'Submission patterns by day of week',
    category: 'activity'
  },
  {
    id: 'hourly_activity',
    name: 'Hourly Activity',
    path: '/charts/hourly_activity.html',
    description: 'Submission patterns by hour',
    category: 'activity'
  }
];

const AnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'growth' | 'retention' | 'activity'>('overview');
  const [selectedChart, setSelectedChart] = useState(charts[0].id);

  const filteredCharts = charts.filter(chart => chart.category === activeTab);
  const activeChart = charts.find(c => c.id === selectedChart);

  // 탭 변경 시 해당 탭의 첫 번째 차트 선택
  React.useEffect(() => {
    const firstChart = filteredCharts[0];
    if (firstChart && !filteredCharts.find(c => c.id === selectedChart)) {
      setSelectedChart(firstChart.id);
    }
  }, [activeTab, filteredCharts, selectedChart]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeInUp}
      className="w-full mb-12"
    >
      {/* Key Metrics - Portfolio Style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Churn Prediction Accuracy', value: '100%' },
          { label: 'Analysis Data', value: '1,196' },
          { label: 'User Segments', value: '3' },
          { label: 'Evaluation Consistency', value: '0.924' }
        ].map((metric, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-gray-50 p-4 rounded-lg border border-gray-200"
          >
            <div className="text-sm text-gray-500 mb-1">{metric.label}</div>
            <div className="text-2xl font-bold text-gray-800">{metric.value}</div>
          </motion.div>
        ))}
      </div>

      {/* Tab Navigation - Portfolio Style */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-4">
          {[
            { id: 'overview' as const, label: '📊 Overview' },
            { id: 'growth' as const, label: '📈 Growth Pattern' },
            { id: 'retention' as const, label: '🔄 Retention' },
            { id: 'activity' as const, label: '📅 Activity Pattern' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                // 해당 탭의 첫 번째 차트 선택
                const firstChart = charts.find(c => c.category === tab.id);
                if (firstChart) setSelectedChart(firstChart.id);
              }}
              className={`px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-gray-800 text-gray-800 font-medium'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 차트 선택 및 표시 */}
      <div className="space-y-6">
        {/* 차트 목록 (선택 가능) */}
        <div className="flex flex-wrap gap-2 mb-4">
          {filteredCharts.map((chart) => (
            <button
              key={chart.id}
              onClick={() => setSelectedChart(chart.id)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                selectedChart === chart.id
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {chart.name}
            </button>
          ))}
        </div>

        {/* 선택된 차트 표시 */}
        {activeChart && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {activeChart.description && (
              <p className="text-gray-600 mb-4 text-sm">{activeChart.description}</p>
            )}
            <ChartViewer
              chartPath={activeChart.path}
              title={activeChart.name}
              height={600}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AnalyticsDashboard;
